import { settlementsQueryDocument } from 'api/graphql';
import { SettlementEntryOrderField } from 'api/graphql/generated/graphql';
import MerchantDashboardLayout from 'layouts/merchant-dashboard/MerchantDashboardLayout';
import ErrorComponent from 'pages/common/error/ErrorComponent';
import { toAmountFilterInput, toInstantFilterInput, toLocalDateFilterInput } from 'pages/common/filters';
import { LISTING_RECORDS_PER_PAGE } from 'pages/platform/constants';
import { useCallback, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useAppDispatch, useAppSelector } from 'redux/merchant/hooks';
import { setPayoutBreakdownPageObj } from 'redux/merchant/slice/payoutSlice';
import { useQuery } from 'urql';
import { CountryCurrency, Currency } from 'utils/currency';

import DashboardRoutesList from '../../constants';
import { getPayoutBreakdownInitialPageState } from './initialStates';
import PayoutBreakdown from './partials/PayoutBreakdown';
import { SettlementsQueryEdges } from './types';

function PayoutBreakdownPage() {
  const dispatch = useAppDispatch();
  const [filterPopupOpen, setFilterPopupOpen] = useState<boolean>(false);

  const { merchantTradingCountry, payoutBreakdownFilterObj, payoutBreakdownPageObj } = useAppSelector((state) => ({
    merchantTradingCountry: state.config.merchantTradingCountry,
    payoutBreakdownFilterObj: state.payout.payoutBreakdownFilterObj,
    payoutBreakdownPageObj: state.payout.payoutBreakdownPageObj,
  }));

  const { first, last, before, after, orderBy } = useMemo(() => payoutBreakdownPageObj, [payoutBreakdownPageObj]);

  const limit = useMemo(() => first ?? last ?? LISTING_RECORDS_PER_PAGE, [first, last]);

  const filters = useMemo(() => {
    const { amount, entryType, transactionId, payoutId, createdOn, availableOn } = payoutBreakdownFilterObj;

    return {
      amount: amount ? toAmountFilterInput(amount) : null,
      currency: CountryCurrency[merchantTradingCountry] as Currency,
      entryTypes: entryType,
      transactionIds: transactionId?.value,
      payoutId: payoutId?.value,
      createdAt: createdOn
        ? toInstantFilterInput(createdOn)
        : !availableOn && !payoutId
        ? toInstantFilterInput({ option: 'is_in_the_last', unit: 'days', period: '7' })
        : null,
      settlementDate: availableOn ? toLocalDateFilterInput(availableOn) : null,
    };
  }, [payoutBreakdownFilterObj, merchantTradingCountry]);

  const [{ fetching, data, error }] = useQuery({
    query: settlementsQueryDocument,
    variables: { last, before, first, after, orderBy, ...filters },
  });

  const handleSort = useCallback(
    (selectedColumn: TableColumn<SettlementsQueryEdges[number]>, sortDirection: SortOrder) => {
      const field = selectedColumn.sortField ? (selectedColumn.sortField as SettlementEntryOrderField) : null;
      const ascending = sortDirection === 'asc';

      dispatch(
        setPayoutBreakdownPageObj({
          ...payoutBreakdownPageObj,
          ...getPayoutBreakdownInitialPageState(),
          orderBy: field ? { field, ascending } : null,
        }),
      );
    },
    [dispatch, payoutBreakdownPageObj],
  );

  const toggleFilter = useCallback(() => {
    setFilterPopupOpen((wasOpened) => !wasOpened);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (!data?.settlementEntries) return;

      const isFirst = page === 1;
      const isPrev = !isFirst && page === payoutBreakdownPageObj.page - 1;
      const isNext = page === payoutBreakdownPageObj.page + 1;
      const isLast = !isFirst && !isNext && page === Math.ceil(data.settlementEntries.totalCount / limit);

      dispatch(
        setPayoutBreakdownPageObj({
          ...payoutBreakdownPageObj,
          page,
          last: isLast || isPrev ? limit : null,
          before: !isLast && isPrev ? data.settlementEntries.pageInfo.startCursor ?? null : null,
          first: isFirst || isNext ? limit : null,
          after: !isFirst && isNext ? data.settlementEntries.pageInfo.endCursor ?? null : null,
        }),
      );
    },
    [dispatch, data?.settlementEntries, limit, payoutBreakdownPageObj],
  );

  const handlePerRowsChange = useCallback(
    async (newPerPage: number, page: number) => {
      dispatch(
        setPayoutBreakdownPageObj({
          ...payoutBreakdownPageObj,
          page,
          last: payoutBreakdownPageObj.last !== null ? newPerPage : null,
          first: payoutBreakdownPageObj.first !== null ? newPerPage : null,
        }),
      );
    },
    [dispatch, payoutBreakdownPageObj],
  );

  return (
    <MerchantDashboardLayout activeTab={DashboardRoutesList.PAYOUT_ROUTE} title="Payout breakdown">
      {!!error && <ErrorComponent bodyText={error.message} />}
      <PayoutBreakdown
        payoutList={data?.settlementEntries?.edges ?? []}
        fetchListLoader={fetching}
        totalRows={data?.settlementEntries?.totalCount ?? 0}
        handlePerRowsChange={handlePerRowsChange}
        handlePageChange={handlePageChange}
        limit={limit}
        toggleFilter={toggleFilter}
        activePage={payoutBreakdownPageObj.page}
        handleSort={handleSort}
        filterPopupOpen={filterPopupOpen}
      />
    </MerchantDashboardLayout>
  );
}

export default PayoutBreakdownPage;
